import { useFormContext } from 'react-hook-form';
import React from 'react';

interface Validators<T> {
    required?: boolean
    minLength?: number
    maxLength?: number
    email?: boolean
    url?: boolean
    number?: boolean
    pattern?: RegExp
    custom?: (value: T) => boolean | string
}

interface FormExport<T> {
    isValid: boolean
    isDisabled: boolean
    value: T|undefined
    setValue: (value: T|null|undefined) => void
    error?: string
}

interface UseMaybeFormProps<T> {
    name: string
    validators?: Validators<T>
    value?: T,
    defaultValue?: T,
    disabled?: boolean
}

const useFormState = <T>(props: UseMaybeFormProps<T>): FormExport<T> => {
    const ctx = useFormContext();
    const [localState, setLocalState] = React.useState<T|undefined>(props.value || props.defaultValue);

    React.useEffect(() => {
        if (ctx) {
            ctx.register(props.name, {
                required: props.validators?.required ? {
                    value: true,
                    message: 'This field is required.',
                } : undefined,
                minLength: props.validators?.minLength ? {
                    value: props.validators.minLength,
                    message: `This field is has to be at least ${props.validators.minLength} characters long.`,
                } : undefined,
                maxLength: props.validators?.maxLength ? {
                    value: props.validators.maxLength,
                    message: `This field must be under ${props.validators.maxLength} characters.`,
                } : undefined,
                pattern: props.validators?.pattern ? {
                    value: props.validators.pattern,
                    message: 'The formatting of this field is incorrect.',
                } : undefined,
                validate: props.validators?.custom,
                value: props.defaultValue,
            });
        }
    }, [
        ctx,
        props.defaultValue,
        props.name,
        props.validators?.custom,
        props.validators?.maxLength,
        props.validators?.minLength,
        props.validators?.pattern,
        props.validators?.required,
    ]);

    React.useEffect(() => {
        if (ctx) {
            if (props.value !== ctx.getValues(props.name)) {
                ctx.setValue(props.name, props.value);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.value]);

    const setter = React.useCallback((value: T|null|undefined) => {
        if (value === null || value === undefined) {
            if (ctx) {
                ctx.clearErrors(props.name);
                ctx.setValue(props.name, undefined);
            } else {
                setLocalState(undefined);
            }
        } else if (ctx) {
            ctx.clearErrors(props.name);
            ctx.setValue(props.name, value);
        } else {
            setLocalState(undefined);
        }
    }, [ctx, props.name]);

    if (!ctx) {
        return {
            isDisabled: !!props.disabled,
            isValid: (() => {
                if (props.validators?.required) {
                    return localState !== undefined;
                }
                return true;
            })(),
            error: (() => {
                if (props.validators?.required && localState === undefined) {
                    return 'Required';
                }
                return undefined;
            })(),
            value: localState,
            setValue: setter,
        };
    }

    return {
        isDisabled: !!props.disabled,
        isValid: !ctx.formState.errors[props.name] && ctx.formState.touchedFields[props.name],
        error: ctx.formState.errors[props.name]?.message as string || undefined,
        value: ctx.watch(props.name),
        setValue: setter,
    };
};

export default useFormState;
